import { useEffect, useRef } from "react";
import styles from './OtpField.module.scss'

type Props = {
    /** Array of single‐digit strings, length = number of boxes */
    value: string[]
    /** Called with the new array whenever any digit changes */
    onChange: (next: string[]) => void
    /** Only show this text if touched && error */
    error?: string
    /** Only show error if this is true */
    touched?: boolean
    /** Number of digits */
    length?: number
}

export const OtpField = ({
    value,
    onChange,
    error,
    touched,
    length = 6,
}: Props) => {
    // Ensure value always has the right length
    const digits = Array.from({ length }, (_, i) => value[i] || '')

    const inputs = useRef<HTMLInputElement[]>([])

    // If parent ever resets to `[]`, re‐initialize
    useEffect(() => {
        if (value.length !== length) {
            onChange(Array(length).fill(''))
        }
    }, [length, onChange, value])

    const handleChange = (idx: number, v: string) => {
        const next = [...digits]
        next[idx] = v.replace(/\D/, '') // digits only
        onChange(next)
        if (v && idx < length - 1) {
            inputs.current[idx + 1]?.focus()
        }
    }

    const handleKeyDown = (idx: number, e: React.KeyboardEvent) => {
        if (e.key === 'Backspace' && !digits[idx] && idx > 0) {
            inputs.current[idx - 1]?.focus()
        }
        if (e.key === 'ArrowLeft' && idx > 0) {
            inputs.current[idx - 1]?.focus()
        }
        if (e.key === 'ArrowRight' && idx < length - 1) {
            inputs.current[idx + 1]?.focus()
        }
    }

    return (
        <div className={styles.otpRootContainer}>
            <div className={styles.label}>
                Enter OTP
            </div>
            <div className={styles.otpContainer}>
                {digits.map((digit, idx) => (
                    <div key={idx} className={`${styles.otpInputWrapper}  ${touched && error ? styles.invalid : ''
                        }`}>
                        <input
                            autoFocus={idx === 0}
                            type="text"
                            maxLength={1}
                            className={`${styles.otpInputBox} ${idx == 0 ? 'otpInputBoxFirst' : ''}`}
                            value={digit}
                            ref={el => el && (inputs.current[idx] = el)}
                            onChange={e => handleChange(idx, e.target.value)}
                            onKeyDown={e => handleKeyDown(idx, e)}
                        />
                    </div>
                ))}
            </div>
            {touched && error && <div className={styles.errorMessage}>{error}</div>}
        </div>
    )
}